/**
 * Base class for DOM components that need lifecycle management.
 *
 * Components are mounted/unmounted automatically as elements are added/removed from the DOM.
 * Use `useHandler` to register event listeners or subscriptions that should be cleaned up on unmount.
 *
 * Example usage:
 *
 * ```js
 * registerFunctionalComponent('.some-btn-selector', 'MyBtnComponent', MyBtnComponent)
 *
 * function MyBtnComponent(component) {
 *   component.useHandler(() => {
 *     component.element.addEventListener('click', doSomethingOnClick)
 *     return () => component.element.removeEventListener('click', doSomethingOnClick)
 *   })
 *   function doSomethingOnClick() {
 *     console.log('clicked')
 *   }
 * }
 * ```
 *
 * @example
 * // Register a component for all elements matching selector
 * registerFunctionalComponent('.js-c-dropdown', 'DropdownComponent', initDropdown)
 *
 * @see registerFunctionalComponent
 */
export class Component {
  unsubscribers = []
  mounted = false
  name = 'BaseComponent'

  /**
   * @private
   * @type {boolean}
   */
  static _registered = false
  /**
   * @private
   * @type {WeakMap<Element, Component>}
   */
  static _mountedInstances = new WeakMap()

  /**
   * @param {Element} element - The DOM element this component wraps.
   */
  constructor(element) {
    /**
     * The DOM element associated with this component instance.
     * @type {Element}
     */
    this.element = element
  }

  /**
   * Register a handler (such as an event listener or subscription) that will be automatically cleaned up on unmount.
   *
   * @param {() => (() => void)} fnThatSubscribesAndReturnsCleanup - Function that subscribes to something and returns a cleanup function.
   */
  useHandler(fnThatSubscribesAndReturnsCleanup) {
    const off = fnThatSubscribesAndReturnsCleanup()
    this.unsubscribers.push(off)
  }

  /**
   * Unmount the component, cleaning up all handlers and removing from registry.
   * Calls `willUnmount` if defined.
   */
  unmount() {
    this.willUnmount?.()
    this.unsubscribers.forEach(fn => fn())
    this.unsubscribers = []
    this.mounted = false
    this.#unlistAsMounted()
  }

  /**
   * Mount the component, calling `initialize` if defined, and registering as mounted.
   */
  mount() {
    this.initialize?.()
    this.#listAsMounted()
    this.mounted = true
  }

  #listAsMounted() {
    this.constructor._mountedInstances.set(this.element, this)
  }

  #unlistAsMounted() {
    this.constructor._mountedInstances.delete(this.element)
  }

  /**
   * Get the mounted component instance for a given DOM node, if any.
   * @param {Element} node
   * @returns {Component|undefined}
   */
  static getInstance(node) {
    return this._mountedInstances.get(node)
  }

  /**
   * Register a component class for a selector, mounting all existing and future matching elements.
   *
   * @param {string} selector - CSS selector for elements to mount as this component.
   * @param {typeof Component} [ComponentClass=this] - The component class to instantiate.
   */
  static register(selector, ComponentClass = this) {
    if (ComponentClass._registered) return

    // Mount any existing elements
    document.querySelectorAll(selector).forEach(node => {
      if (!ComponentClass.getInstance(node)) {
        new ComponentClass(node).mount()
      }
    })

    observeDOMChanges(selector, ComponentClass)
    ComponentClass._registered = true
  }
}

/**
 * Register a functional component for all elements matching a selector.
 *
 * @param {string} selector - CSS selector for elements to mount as this component.
 * @param {string} name - Name for the component (for debugging).
 * @param {(component: Component) => void} initializer - Function to initialize the component instance.
 */
export function registerFunctionalComponent(selector, name, initializer) {
  const ComponentClass = {
    [name]: class extends Component {
      constructor(element) {
        super(element)
        this.name = name
      }

      initialize() {
        initializer(this)
      }
    },
  }[name]

  ComponentClass.register(selector)
}

function observeDOMChanges(selector, ComponentClass) {
  const observer = new window.MutationObserver(mutations => {
    for (const mutation of mutations) {
      // Handle removed nodes
      mutation.removedNodes.forEach(node => {
        if (isElementNode(node)) unmountNodeAndChildren(node, selector, ComponentClass)
      })

      // Handle added nodes
      mutation.addedNodes.forEach(node => {
        if (isElementNode(node)) mountNodeAndChildren(node, selector, ComponentClass)
      })
    }
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}

function unmountNodeAndChildren(node, selector, ComponentClass) {
  const instance = ComponentClass.getInstance(node)
  if (instance) instance.unmount()

  node.querySelectorAll(selector).forEach(subNode => {
    const childInstance = ComponentClass.getInstance(subNode)
    if (childInstance) childInstance.unmount()
  })
}

function mountNodeAndChildren(node, selector, ComponentClass) {
  if (node.matches(selector) && !ComponentClass.getInstance(node)) {
    new ComponentClass(node).mount()
  }

  node.querySelectorAll(selector).forEach(subNode => {
    if (!ComponentClass.getInstance(subNode)) {
      new ComponentClass(subNode).mount()
    }
  })
}

function isElementNode(node) {
  return node && node.nodeType === 1
}
